import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AIImageReferenceInput, { AIImageReferenceHandle } from '../shared/AIImageReferenceInput';
import {
  Sparkles,
  Clock,
  Edit2,
  Trash2,
  Calendar,
  RefreshCw,
  CheckCircle,
  Image,
  Zap,
  ChevronLeft,
  ChevronRight,
  Film,
  MessageSquare,
  Lightbulb,
  TrendingUp,
  Users,
  Newspaper,
  Hash,
  X,
  Eye,
  Send,
  AtSign
} from 'lucide-react';
import Button from '../ui/Button';
import BrandContextBanner from '../shared/BrandContextBanner';
import VariationControls from '../shared/VariationControls';
import SocialAutoRoutine from './SocialAutoRoutine';
import AiCostBadge from '../ai/AiCostBadge';

// Platform icons
const PlatformIcons: Record<string, React.FC<{ className?: string }>> = {
  instagram: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  facebook: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  twitter: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  linkedin: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  youtube: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  tiktok: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
  threads: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.106-1.146 3.451-1.175 1.016-.022 1.96.139 2.818.468-.073-.938-.306-1.683-.7-2.216-.453-.614-1.143-.936-2.053-.958l-.023-.003c-.689 0-1.58.142-2.433.907l-1.416-1.486c1.249-1.12 2.664-1.538 4.001-1.538h.036c1.478.034 2.689.548 3.6 1.528.857.922 1.366 2.2 1.514 3.8.475.19.917.422 1.32.695 1.198.813 2.074 1.922 2.533 3.205.752 2.106.593 4.932-1.635 7.11C18.581 23.087 15.994 23.973 12.186 24z"/>
    </svg>
  ),
  instagram_reels: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
      <path d="M9.5 7.5l6 4.5-6 4.5z" fill="white"/>
    </svg>
  ),
  youtube_shorts: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 14.65v-5.3L15 12l-5 2.65zm7.77-4.33c-.77-.32-1.2-.5-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.24-2.53-5.07-1.56L6 6.94c-1.29.68-2.07 2.04-2 3.49.07 1.42.93 2.67 2.22 3.25.03.01 1.2.5 1.2.5L6 14.93c-1.83.97-2.53 3.24-1.56 5.07.97 1.83 3.24 2.53 5.07 1.56l8.5-4.5c1.29-.68 2.06-2.04 1.99-3.49-.07-1.42-.94-2.68-2.23-3.25z"/>
    </svg>
  )
};
// Stories reaproveitam o ícone da rede-mãe (IG/FB) — visualmente reconhecível.
PlatformIcons.instagram_stories = PlatformIcons.instagram;
PlatformIcons.facebook_stories = PlatformIcons.facebook;

const platformColors: Record<string, string> = {
  instagram: 'bg-gradient-to-br from-purple-500 to-pink-500',
  instagram_reels: 'bg-gradient-to-br from-purple-600 to-pink-600',
  facebook: 'bg-blue-600',
  twitter: 'bg-gray-900 dark:bg-gray-700',
  linkedin: 'bg-blue-700',
  youtube: 'bg-red-600',
  youtube_shorts: 'bg-red-500',
  tiktok: 'bg-gray-900 dark:bg-gray-700',
  threads: 'bg-gray-900 dark:bg-gray-700',
  instagram_stories: 'bg-gradient-to-br from-purple-500 to-orange-400',
  facebook_stories: 'bg-gradient-to-br from-blue-600 to-cyan-400'
};

// Platform configuration with conflicts (same as AdvancedComposer)
const platformConfig: Record<string, {
  name: string;
  videoFormat: 'vertical' | 'horizontal' | 'any';
  conflictsWith: string[];
}> = {
  instagram: { name: 'Instagram', videoFormat: 'any', conflictsWith: [] },
  instagram_reels: { name: 'Reels', videoFormat: 'vertical', conflictsWith: ['youtube'] },
  facebook: { name: 'Facebook', videoFormat: 'any', conflictsWith: [] },
  twitter: { name: 'X', videoFormat: 'any', conflictsWith: [] },
  linkedin: { name: 'LinkedIn', videoFormat: 'any', conflictsWith: [] },
  youtube: { name: 'YouTube', videoFormat: 'horizontal', conflictsWith: ['youtube_shorts', 'tiktok', 'instagram_reels'] },
  youtube_shorts: { name: 'Shorts', videoFormat: 'vertical', conflictsWith: ['youtube'] },
  tiktok: { name: 'TikTok', videoFormat: 'vertical', conflictsWith: ['youtube'] },
  threads: { name: 'Threads', videoFormat: 'any', conflictsWith: [] },
  instagram_stories: { name: 'Instagram Stories', videoFormat: 'vertical', conflictsWith: [] },
  facebook_stories: { name: 'Facebook Stories', videoFormat: 'vertical', conflictsWith: [] }
};

const postTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  educational: Lightbulb,
  inspirational: Sparkles,
  tips: CheckCircle,
  engagement: MessageSquare,
  behind_scenes: Users,
  news: Newspaper
};

const postTypeLabels: Record<string, string> = {
  educational: 'Educativo',
  inspirational: 'Inspirador',
  tips: 'Dicas',
  engagement: 'Engajamento',
  behind_scenes: 'Bastidores',
  news: 'Notícia'
};

interface AISuggestion {
  id: string;
  content: string;
  image_url?: string;
  media_urls?: string[];
  platforms: string[];
  suggested_platforms?: string[];
  suggested_time: string;
  topic?: string;
  hashtags: string[];
  tone?: string;
  content_type?: string;
  media_hint?: string;
  post_type?: string;
  metadata?: any;
  created_at: string;
}

interface ConnectedAccount {
  id: string;
  platform: string;
  username: string;
  connected: boolean;
}

interface AISuggestionsProps {
  suggestions?: AISuggestion[];
  loading?: boolean;
  generating?: boolean;
  connectedAccounts?: ConnectedAccount[];
  projectData?: { name?: string; description?: string };
  onSchedule?: (suggestion: AISuggestion) => void;
  onEdit?: (suggestion: AISuggestion) => void;
  onRegenerate?: (suggestion: AISuggestion) => void;
  onDiscard?: (id: string) => void;
  onPublishNow?: (suggestion: AISuggestion) => void;
  onGenerateMore?: (customPrompt?: string, count?: number, platforms?: string[], opts?: { ignorePattern?: boolean; variationDirection?: string; generateImages?: boolean; referenceImageUrl?: string; tone?: string; imageStyle?: string; aspectRatio?: string; mentions?: string[] }) => void;
  onCancelGeneration?: () => void;
}

const ITEMS_PER_PAGE = 6;

const AISuggestions: React.FC<AISuggestionsProps> = ({
  suggestions = [],
  loading = false,
  generating = false,
  connectedAccounts = [],
  projectData,
  onSchedule,
  onEdit,
  onRegenerate,
  onDiscard,
  onPublishNow,
  onGenerateMore,
  onCancelGeneration
}) => {
  const { t } = useTranslation();
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [viewingSuggestion, setViewingSuggestion] = useState<AISuggestion | null>(null);
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [mentionsList, setMentionsList] = useState<string[]>([]);
  const [mentionInput, setMentionInput] = useState<string>('');
  const [showPromptInput, setShowPromptInput] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [postCount, setPostCount] = useState<number>(4);
  const [ignorePattern, setIgnorePattern] = useState<boolean>(false);
  const [variationDirection, setVariationDirection] = useState<string>('');
  const [generateImages, setGenerateImages] = useState<boolean>(false);
  // Content tone + image style/ratio — same controls the composer has so the
  // suggestion generator respects user-defined creative direction.
  const [aiTone, setAiTone] = useState<string>('professional');
  const [aiImageStyle, setAiImageStyle] = useState<string>('professional');
  const [aiImageRatio, setAiImageRatio] = useState<string>('1:1');
  const aiRefHandle = useRef<AIImageReferenceHandle | null>(null);

  // Quando só Stories estão selecionados, sugere 9:16 automaticamente (formato
  // nativo de story). O usuário ainda pode trocar manualmente depois.
  useEffect(() => {
    const onlyStories = selectedPlatforms.length > 0 &&
      selectedPlatforms.every(p => p === 'instagram_stories' || p === 'facebook_stories');
    if (onlyStories && aiImageRatio !== '9:16') {
      setAiImageRatio('9:16');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatforms]);

  const addMention = () => {
    const raw = mentionInput.trim().replace(/^@+/, '');
    if (!raw) return;
    if (!mentionsList.includes(raw)) setMentionsList(prev => [...prev, raw]);
    setMentionInput('');
  };

  const formatSuggestedTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    const dayOfWeek = date.toLocaleDateString(undefined, { weekday: 'short' });
    const dateFormatted = date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });

    if (date.toDateString() === now.toDateString()) {
      return `Hoje às ${time}`;
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return `Amanhã às ${time}`;
    }

    return `${dayOfWeek}, ${dateFormatted} às ${time}`;
  };

  // Check if suggestion is "new" (created in last 30 minutes)
  const isNewSuggestion = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMinutes = (now.getTime() - created.getTime()) / (1000 * 60);
    return diffMinutes < 30;
  };

  // Get unique connected platforms and derived platforms.
  // This picker is for ORGANIC posts — ads accounts (google_ads / facebook_ads /
  // linkedin_ads) must not show up here.
  const ADS_PLATFORMS = new Set(['google_ads', 'facebook_ads', 'linkedin_ads']);
  const allAvailablePlatforms = (() => {
    const connected = connectedAccounts
      .filter(account => account.connected && !ADS_PLATFORMS.has(account.platform))
      .map(account => account.platform)
      .filter((platform, index, self) => self.indexOf(platform) === index);

    const platforms = [...connected];
    if (connected.includes('youtube') && !platforms.includes('youtube_shorts')) {
      platforms.push('youtube_shorts');
    }
    if (connected.includes('instagram') && !platforms.includes('instagram_reels')) {
      platforms.push('instagram_reels');
    }
    // Stories herdam a conexão da rede-mãe (IG/FB) — derivadas como reels/shorts.
    if (connected.includes('instagram') && !platforms.includes('instagram_stories')) {
      platforms.push('instagram_stories');
    }
    if (connected.includes('facebook') && !platforms.includes('facebook_stories')) {
      platforms.push('facebook_stories');
    }
    return platforms;
  })();

  // Check if a platform conflicts with currently selected platforms
  const getConflictingPlatform = (platformId: string): string | null => {
    const config = platformConfig[platformId];
    if (!config) return null;

    for (const selected of selectedPlatforms) {
      if (config.conflictsWith.includes(selected)) {
        return selected;
      }
      const selectedConfig = platformConfig[selected];
      if (selectedConfig?.conflictsWith.includes(platformId)) {
        return selected;
      }
    }
    return null;
  };

  // Toggle platform selection with conflict checking
  const togglePlatform = (platformId: string) => {
    if (selectedPlatforms.includes(platformId)) {
      setSelectedPlatforms(prev => prev.filter(p => p !== platformId));
    } else {
      const conflict = getConflictingPlatform(platformId);
      if (conflict) {
        // Can't select - show which platform conflicts
        return;
      }
      setSelectedPlatforms(prev => [...prev, platformId]);
    }
    setCurrentPage(1);
  };

  // Separate new and old suggestions (no filtering - platforms are for generation only)
  const newSuggestions = suggestions.filter(s => isNewSuggestion(s.created_at));
  const oldSuggestions = suggestions.filter(s => !isNewSuggestion(s.created_at));

  // Pagination for old suggestions only
  const totalPages = Math.ceil(oldSuggestions.length / ITEMS_PER_PAGE);
  const paginatedOldSuggestions = oldSuggestions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const renderSuggestionCard = (suggestion: AISuggestion, isNew: boolean = false) => {
    const PostTypeIcon = suggestion.post_type ? postTypeIcons[suggestion.post_type] : Sparkles;
    const displayPlatforms = suggestion.suggested_platforms || suggestion.platforms;

    return (
      <div
        key={suggestion.id}
        className={`bg-white dark:bg-gray-800 rounded-xl border overflow-hidden hover:shadow-lg transition-shadow group ${
          isNew
            ? 'border-purple-300 dark:border-purple-600 ring-2 ring-purple-100 dark:ring-purple-900/30'
            : 'border-gray-200 dark:border-gray-700'
        }`}
      >
        {/* Header with post type and new badge */}
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {suggestion.post_type && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
                {PostTypeIcon && <PostTypeIcon className="h-3 w-3" />}
                {postTypeLabels[suggestion.post_type] || suggestion.post_type}
              </span>
            )}
            {isNew && (
              <span className="px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full">
                NOVA
              </span>
            )}
          </div>
          {suggestion.media_hint && (
            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              {suggestion.media_hint.includes('vídeo') ? <Film className="h-3 w-3" /> : <Image className="h-3 w-3" />}
              {suggestion.media_hint}
            </span>
          )}
        </div>

        {/* Generated image preview (if any) */}
        {(() => {
          const previewUrl = (Array.isArray(suggestion.media_urls) && suggestion.media_urls.length > 0)
            ? suggestion.media_urls[0]
            : suggestion.image_url;
          return previewUrl ? (
            <div className="px-4 pb-3">
              <img
                src={previewUrl}
                alt={suggestion.topic || ''}
                className="w-full aspect-square object-cover rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                loading="lazy"
              />
            </div>
          ) : null;
        })()}

        {/* Content */}
        <div className="px-4 pb-2">
          <p className="text-sm text-gray-900 dark:text-white line-clamp-3 mb-3">
            {suggestion.content}
          </p>

          {/* Hashtags */}
          {suggestion.hashtags && suggestion.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              <Hash className="h-3 w-3 text-gray-400 mt-0.5" />
              {suggestion.hashtags.slice(0, 5).map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-primary-600 dark:text-primary-400"
                >
                  {tag.startsWith('#') ? tag : `#${tag}`}
                </span>
              ))}
              {suggestion.hashtags.length > 5 && (
                <span className="text-xs text-gray-400">+{suggestion.hashtags.length - 5}</span>
              )}
            </div>
          )}
        </div>

        {/* Suggested Platforms */}
        <div className="px-4 pb-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Redes sugeridas:</p>
          <div className="flex flex-wrap gap-1.5">
            {displayPlatforms.map((platform) => {
              const Icon = PlatformIcons[platform];
              const platformName = platform.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
              return (
                <div
                  key={platform}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-white text-xs ${platformColors[platform] || 'bg-gray-500'}`}
                  title={platformName}
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  <span className="hidden sm:inline">{platformName}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time */}
        <div className="px-4 pb-3 flex items-center justify-between">
          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Clock className="h-3 w-3" />
            {formatSuggestedTime(suggestion.suggested_time)}
          </span>
          {suggestion.tone && (
            <span className="text-xs text-gray-400 capitalize">Tom: {suggestion.tone}</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <Button
            variant="primary"
            size="sm"
            onClick={() => onSchedule?.(suggestion)}
            className="flex-1 gap-1"
          >
            <Calendar className="h-4 w-4" />
            {t('social.schedule', 'Agendar')}
          </Button>
          {onPublishNow && (
            <button
              onClick={() => onPublishNow(suggestion)}
              className="p-2 rounded-lg text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
              title={t('social.publishNow', 'Publicar agora')}
            >
              <Send className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => setViewingSuggestion(suggestion)}
            className="p-2 rounded-lg text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title={t('common.view', 'Ver')}
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit?.(suggestion)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={t('common.edit', 'Editar')}
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onRegenerate?.(suggestion)}
            className="p-2 rounded-lg text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
            title={t('social.regenerate', 'Regenerar')}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDiscard?.(suggestion.id)}
            className="p-2 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={t('common.delete', 'Descartar')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Show generating banner prominently
  if (generating) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
          <div className="flex flex-col md:flex-row items-center gap-3 sm:gap-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <Sparkles className="h-10 w-10 animate-pulse" />
              </div>
              <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-ping" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">
                {t('social.aiWorking', 'IA está trabalhando... v2')}
              </h3>
              <p className="text-white/90 text-base mb-4">
                {t('social.aiWorkingDesc', 'Analisando seu perfil e gerando sugestões personalizadas...')}
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span className="text-sm font-medium">{t('common.generating', 'Gerando sugestões...')}</span>
                </div>
                {onCancelGeneration && (
                  <button
                    onClick={() => onCancelGeneration()}
                    type="button"
                    className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white rounded-full px-4 py-2 transition-colors cursor-pointer"
                  >
                    <X className="h-4 w-4" />
                    <span className="text-sm font-medium">{t('common.cancel', 'Cancelar')}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="mt-6 bg-white/20 rounded-full h-2 overflow-hidden">
            <div className="h-full w-1/2 bg-white/60 rounded-full animate-loading" />
          </div>
        </div>

        {/* Show existing suggestions while generating */}
        {suggestions.length > 0 && (
          <div className="opacity-50 pointer-events-none">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suggestions.slice(0, 3).map((s) => renderSuggestionCard(s))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
              <div className="h-4 w-1/3 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
              <div className="flex gap-2">
                <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
                <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Recurring social routine (shared) */}
      <SocialAutoRoutine />

      {/* AI Generate Box */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 p-[1px]">
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-6">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-purple-100/50 to-transparent dark:from-purple-900/20 rounded-full -translate-y-32 translate-x-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-pink-100/50 to-transparent dark:from-pink-900/20 rounded-full translate-y-24 -translate-x-24" />

          <div className="relative">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/30">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Gerador de Ideias
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {suggestions.length > 0
                      ? `${suggestions.length} sugestões salvas • ${newSuggestions.length} novas`
                      : 'Deixe a IA criar conteúdo para você'}
                  </p>
                </div>
              </div>
            </div>

            {/* Brand context banner (shared) */}
            <div className="mb-5">
              <BrandContextBanner ignorePattern={ignorePattern} />
            </div>

            {/* Variation controls (shared) */}
            <div className="mb-5 rounded-xl border border-purple-200 dark:border-purple-800 bg-white/60 dark:bg-gray-900/40 p-3">
              <VariationControls
                ignorePattern={ignorePattern}
                onIgnoreChange={setIgnorePattern}
                variationDirection={variationDirection}
                onVariationChange={setVariationDirection}
              />
            </div>

            {/* Platform Selection - First */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Selecione as redes sociais
                </p>
                {selectedPlatforms.length > 0 && (
                  <button
                    onClick={() => setSelectedPlatforms([])}
                    className="text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400"
                  >
                    Limpar
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {allAvailablePlatforms.map(platform => {
                  const Icon = PlatformIcons[platform];
                  const config = platformConfig[platform];
                  const isSelected = selectedPlatforms.includes(platform);
                  const conflictingPlatform = !isSelected ? getConflictingPlatform(platform) : null;
                  const isDisabled = !!conflictingPlatform;

                  return (
                    <button
                      key={platform}
                      onClick={() => !isDisabled && togglePlatform(platform)}
                      disabled={isDisabled}
                      title={isDisabled ? `Conflita com ${platformConfig[conflictingPlatform!]?.name}` : config?.name}
                      className={`
                        inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
                        ${isSelected
                          ? `${platformColors[platform]} text-white shadow-md`
                          : isDisabled
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }
                      `}
                    >
                      {Icon && <Icon className="h-4 w-4" />}
                      {config?.name || platform}
                      {isSelected && (
                        <span className="ml-1 text-white/80">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedPlatforms.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  Selecione pelo menos uma rede social para gerar ideias
                </p>
              )}
            </div>

            {/* Theme Input */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tema ou assunto (opcional)
                </label>
                {customPrompt && (
                  <button
                    onClick={() => setCustomPrompt('')}
                    className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    Limpar
                  </button>
                )}
              </div>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Ex: dicas de produtividade, lançamento de produto, bastidores, promoção especial..."
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Menções */}
            <div className="mb-5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <AtSign className="h-4 w-4" />
                {t('social.mentions', 'Menções')} <span className="text-xs text-gray-400 font-normal">({t('common.optional', 'opcional')})</span>
              </label>
              {mentionsList.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {mentionsList.map((h) => (
                    <span
                      key={h}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-lg"
                    >
                      @{h}
                      <button onClick={() => setMentionsList(prev => prev.filter(x => x !== h))} className="hover:text-blue-900 dark:hover:text-blue-100">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={mentionInput}
                  onChange={(e) => setMentionInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMention(); } }}
                  placeholder={t('social.addMention', '@usuario para mencionar...')}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <Button variant="secondary" size="sm" onClick={addMention}>
                  <AtSign className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {t('social.mentionsHint', 'As menções (@) serão incluídas no texto das sugestões geradas.')}
              </p>
            </div>

            {/* Tone + Image Style + Aspect Ratio */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t('social.tone', 'Tom')}
                </label>
                <select
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="professional">{t('social.tones.professional', 'Profissional')}</option>
                  <option value="casual">{t('social.tones.casual', 'Casual')}</option>
                  <option value="friendly">{t('social.tones.friendly', 'Amigável')}</option>
                  <option value="humorous">{t('social.tones.humorous', 'Humor')}</option>
                  <option value="inspirational">{t('social.tones.inspirational', 'Inspirador')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t('social.imageStyle', 'Estilo da Imagem')}
                </label>
                <select
                  value={aiImageStyle}
                  onChange={(e) => setAiImageStyle(e.target.value)}
                  disabled={!generateImages}
                  className="w-full px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="professional">{t('social.imageStyles.professional', 'Profissional')}</option>
                  <option value="realistic">{t('social.imageStyles.realistic', 'Realista')}</option>
                  <option value="artistic">{t('social.imageStyles.artistic', 'Artístico')}</option>
                  <option value="minimal">{t('social.imageStyles.minimal', 'Minimalista')}</option>
                  <option value="vibrant">{t('social.imageStyles.vibrant', 'Vibrante')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t('social.aspectRatio', 'Proporção')}
                </label>
                <select
                  value={aiImageRatio}
                  onChange={(e) => setAiImageRatio(e.target.value)}
                  disabled={!generateImages}
                  className="w-full px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="1:1">{t('social.aspectRatios.1:1', '1:1 (Feed)')}</option>
                  <option value="4:5">{t('social.aspectRatios.4:5', '4:5 (Instagram)')}</option>
                  <option value="16:9">{t('social.aspectRatios.16:9', '16:9 (YouTube)')}</option>
                  <option value="9:16">{t('social.aspectRatios.9:16', '9:16 (Stories/Reels)')}</option>
                </select>
              </div>
            </div>

            {/* Quantity Selector & Generate Button */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Quantidade:</span>
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <button
                      key={num}
                      onClick={() => setPostCount(num)}
                      className={`w-10 h-10 text-sm font-bold rounded-lg transition-all ${
                        postCount === num
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30 scale-105'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={generateImages}
                  onChange={(e) => setGenerateImages(e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span>
                  {t('social.suggestions.withImage', 'Gerar com imagem')}
                  <span className="text-xs text-gray-400 ml-1">
                    {t('social.suggestions.withImageHint', '(demora alguns segundos a mais)')}
                  </span>
                </span>
              </label>

              <div className="flex-1" />

              <Button
                variant="primary"
                onClick={async () => {
                  if (!onGenerateMore) return;
                  let referenceImageUrl: string | undefined;
                  if (generateImages) {
                    try {
                      referenceImageUrl = (await aiRefHandle.current?.resolve()) || undefined;
                    } catch (err: any) {
                      // Reference upload failed — let the user retry rather than silently
                      // firing a generation without the reference they configured.
                      const msg = err?.response?.data?.message || err?.message
                        || (t('imageStudio.reference.uploadError', 'Failed to upload reference image.') as string);
                      (await import('react-hot-toast')).default.error(msg);
                      return;
                    }
                  }
                  onGenerateMore(customPrompt || undefined, postCount, selectedPlatforms, {
                    ignorePattern,
                    variationDirection: variationDirection.trim() || undefined,
                    generateImages,
                    referenceImageUrl,
                    tone: aiTone,
                    imageStyle: aiImageStyle,
                    aspectRatio: aiImageRatio,
                    mentions: mentionsList.length > 0 ? mentionsList : undefined
                  });
                }}
                className="gap-2 w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 border-0 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={generating || selectedPlatforms.length === 0}
              >
                <Zap className="h-5 w-5" />
                <span className="font-semibold">Gerar {postCount} Ideias</span>
              </Button>
            </div>
            <div className="mt-2 space-y-1">
              <AiCostBadge feature="ai-generation" action="post-suggestions" unitLabel={t('aiCredits.perGeneration', 'por geração')} />
              {generateImages && (
                <AiCostBadge feature="ai-generation" action="content-image" quantity={postCount} />
              )}
            </div>

            {/* Reference image panel — shown only when "Gerar com imagem" is active */}
            {generateImages && (
              <div className="mt-4">
                <AIImageReferenceInput
                  ref={aiRefHandle}
                  uploadReference={async (f) => {
                    const { imageStudioService } = await import('../../services/imageStudioService');
                    return await imageStudioService.uploadReference(f);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Empty State */}
      {suggestions.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <Sparkles className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('social.noSuggestions', 'No suggestions yet')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t('social.useGeneratorAbove', 'Use the generator above to create post ideas')}
          </p>
        </div>
      ) : (
        <>
          {/* New Suggestions Section */}
          {newSuggestions.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Novas Sugestões
                </h4>
                <span className="px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full">
                  {newSuggestions.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {newSuggestions.map((suggestion) => renderSuggestionCard(suggestion, true))}
              </div>
            </div>
          )}

          {/* Divider */}
          {newSuggestions.length > 0 && oldSuggestions.length > 0 && (
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Sugestões anteriores</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            </div>
          )}

          {/* Old Suggestions Section with Pagination */}
          {oldSuggestions.length > 0 && (
            <div>
              {newSuggestions.length === 0 && (
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Todas as Sugestões ({oldSuggestions.length})
                </h4>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedOldSuggestions.map((suggestion) => renderSuggestionCard(suggestion, false))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
            <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h4 className="font-medium text-purple-900 dark:text-purple-100">
              {t('social.aiHint', 'AI Tip')}
            </h4>
            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
              {t('social.aiHintDescription', 'Each suggestion comes with the most suitable social networks for the content type. You can edit, regenerate or schedule directly.')}
            </p>
          </div>
        </div>
      </div>

      {/* Preview modal — visão completa da sugestão */}
      {viewingSuggestion && (() => {
        const s = viewingSuggestion;
        const modalPlatforms = s.suggested_platforms || s.platforms || [];
        const modalImage = (Array.isArray(s.media_urls) && s.media_urls.length > 0)
          ? s.media_urls[0]
          : s.image_url;
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setViewingSuggestion(null)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {t('social.suggestionPreview', 'Pré-visualização da sugestão')}
                </h3>
                <button
                  onClick={() => setViewingSuggestion(null)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {modalImage && (
                  <img src={modalImage} alt="" className="w-full rounded-xl object-cover max-h-80" loading="lazy" />
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {modalPlatforms.map((platform) => {
                    const Icon = PlatformIcons[platform];
                    const platformName = platform.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <span
                        key={platform}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-white text-xs ${platformColors[platform] || 'bg-gray-500'}`}
                      >
                        {Icon && <Icon className="h-3 w-3" />}
                        {platformName}
                      </span>
                    );
                  })}
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{s.content}</p>
                {s.hashtags && s.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {s.hashtags.map((tag, i) => (
                      <span key={i} className="text-xs text-purple-600 dark:text-purple-400">#{tag}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatSuggestedTime(s.suggested_time)}
                  </span>
                  {s.tone && <span className="capitalize">Tom: {s.tone}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => { onSchedule?.(s); setViewingSuggestion(null); }}
                  className="flex-1 gap-1"
                >
                  <Calendar className="h-4 w-4" />
                  {t('social.schedule', 'Agendar')}
                </Button>
                {onPublishNow && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { onPublishNow(s); setViewingSuggestion(null); }}
                    className="gap-1"
                  >
                    <Send className="h-4 w-4" />
                    {t('social.publishNow', 'Publicar agora')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AISuggestions;
